import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, DeviceMobile, Share, Plus } from "@phosphor-icons/react";

const DISMISS_KEY = "wba_install_banner_dismissed";

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isMobile() {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod|Mobile/.test(navigator.userAgent);
}

export default function InstallAppBanner() {
  const [visible, setVisible] = useState(false);
  const [deferred, setDeferred] = useState(null);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // already installed
    if (!isMobile()) return; // only prompt on mobile
    if (localStorage.getItem(DISMISS_KEY) === "1") return;

    // Android / Chrome
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setTimeout(() => setVisible(true), 2500); // give the splash time to disappear first
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS: show manual instructions after a delay
    if (isIOS()) {
      setIos(true);
      const t = setTimeout(() => setVisible(true), 3000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      };
    }
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted" || outcome === "dismissed") {
      localStorage.setItem(DISMISS_KEY, "1");
      setVisible(false);
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="install-banner"
          data-testid="install-app-banner"
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-4 left-4 right-4 z-[100] mx-auto max-w-md border border-cyan/40 bg-ink-800/95 p-4 shadow-[0_0_40px_rgba(0,240,255,0.15)] backdrop-blur-xl md:hidden"
        >
          <button
            data-testid="install-banner-close"
            onClick={dismiss}
            aria-label="Close"
            className="absolute right-2 top-2 p-1 text-slate-400 hover:text-white"
          >
            <X size={16} />
          </button>

          <div className="flex items-start gap-3 pr-6">
            <img
              src="/logos/logo_navbar.png"
              alt="WBA"
              className="h-12 w-12 shrink-0 object-contain"
              draggable={false}
            />
            <div className="min-w-0">
              <p className="font-head text-sm font-extrabold uppercase leading-tight tracking-[0.12em] text-white">
                Installa <span className="text-cyan">Wall Board Academy</span>
              </p>
              <p className="mt-1 font-body text-xs leading-snug text-slate-400">
                {ios
                  ? "Aggiungi la app alla home del tuo iPhone per un accesso rapido, senza barra del browser."
                  : "Installa la app sul tuo dispositivo per un accesso rapido a video, presentazioni e dirette."}
              </p>

              {ios ? (
                <div className="mt-3 space-y-2 border-t border-white/10 pt-3 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-300">
                  <div className="flex items-center gap-2">
                    <span className="flex h-5 w-5 items-center justify-center rounded-sm border border-cyan/40 text-[10px] font-bold text-cyan">1</span>
                    <span>Tocca</span>
                    <Share size={14} weight="regular" className="text-cyan" />
                    <span>in basso</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="flex h-5 w-5 items-center justify-center rounded-sm border border-cyan/40 text-[10px] font-bold text-cyan">2</span>
                    <span>Poi</span>
                    <Plus size={14} weight="bold" className="text-cyan" />
                    <span>"Aggiungi alla Home"</span>
                  </div>
                </div>
              ) : (
                <button
                  data-testid="install-banner-install-btn"
                  onClick={install}
                  disabled={!deferred}
                  className="mt-3 flex items-center gap-2 bg-cyan px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-widest text-ink-900 transition-colors hover:bg-white disabled:opacity-40"
                >
                  <DeviceMobile size={14} weight="bold" /> Installa la App
                </button>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
